import Script from 'next/script'
import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import { useOnMount } from '@captaincause/lib/useOnMount'
import { tm } from '@captaincause/ui/tm'
import { Button } from 'components/Button'
import type { Locales } from '@captaincause/types'

interface GoogleSignInProps {
  mode: 'signin' | 'signup'
  onSuccess: (
    credential: google.accounts.id.CredentialResponse['credential'],
  ) => Promise<void>
  onError?: () => void
  locale: Locales
  className?: string
}

const createFakeGoogleWrapper = (locale: Locales) => {
  const main = document.getElementsByTagName('main')[0]
  const googleLoginWrapper = document.createElement('div')

  googleLoginWrapper.id = 'googleLoginWrapper'
  googleLoginWrapper.style.position = 'absolute'
  googleLoginWrapper.style.top = '-100px'

  main.appendChild(googleLoginWrapper)

  window.google.accounts.id.renderButton(googleLoginWrapper, {
    type: 'standard',
    width: 200,
    locale,
    shape: 'pill',
  })
  const googleLoginWrapperButton = googleLoginWrapper.querySelector(
    'div[role=button]',
  ) as HTMLElement

  return {
    click: () => {
      googleLoginWrapperButton.click()
    },
  }
}

export function GoogleSignIn({
  mode,
  onSuccess,
  onError = undefined,
  locale,
  className = '',
}: GoogleSignInProps) {
  const [googleButton, setGoogleButton] = useState({ click: () => {} })
  const { t } = useTranslation('me.page')
  async function handleCredentialResponse(
    response: google.accounts.id.CredentialResponse,
  ) {
    if (!response) {
      onError?.()
    } else {
      onSuccess(response.credential)
    }
  }

  const initializeSignInButton = () => {
    window.google.accounts.id.initialize({
      client_id: process.env.NEXT_PUBLIC_ME_GOOGLESIGNIN_CLIENT_ID as string,
      callback: handleCredentialResponse,
    })
    setGoogleButton(createFakeGoogleWrapper(locale))
  }

  useOnMount(() => {
    if (window.google) {
      initializeSignInButton()
    }
  })

  return (
    <>
      <Script
        src="https://accounts.google.com/gsi/client"
        strategy="lazyOnload"
        onLoad={initializeSignInButton}
      />
      <Button
        onTouchStart={googleButton?.click}
        onClick={googleButton?.click}
        iconClassName="left-0 relative"
        className={tm(
          'flex w-[324px] items-center justify-center gap-2 rounded-[39px] border border-gray bg-transparent px-7 py-3 text-black hover:bg-light-gray hover:bg-opacity-25 focus:bg-light-gray focus:bg-opacity-25 active:bg-light-gray active:bg-opacity-25',
          className,
        )}
        iconLeft="google"
      >
        <span>{mode === 'signin' ? t('continue-with') : t('signup-with')}</span>
      </Button>
    </>
  )
}
